<template>
  <div :class="rootClasses">
    <label v-if="$slots.label" :for="String($attrs.inputId || '')">
      <slot name="label" />
    </label>

    <!-- @vue-expect-error FIXME: 'String | Date | Date[]' not assignable -->
    <PVCalendar
      v-model="value"
      v-bind="$attrs"
      :inputProps="{ 'aria-required': $attrs.required === true }"
      @focus="focused = true"
      @blur="focused = false"
      @mouseover="hovered = true"
      @mouseleave="hovered = false"
      :dateFormat="dateFormat"
      id="calendar-input"
      ref="calendar"
      :selectionMode="selectionMode"
    >
      <template v-for="(_, slot) of $slots" v-slot:[slot]="scope">
        <slot v-if="slot" :name="slot" v-bind="scope" />
      </template>

      <template #footer v-if="selectionMode === 'range' && showCustomFooter">
        <div class="p-datepicker-buttonbar">
          <div class="flex flex-wrap">
            <div class="w-full md:w-6 p-1 md:p-2">
              <Button
                :label="translate('calendar.todayLabel', 'Today')"
                @click="onTodayButtonClick($event)"
                :class="customTodayButtonClasses"
              />
            </div>
            <div class="w-full md:w-6 p-1 md:p-2">
              <Button
                :label="translate('calendar.yesterdayLabel', 'Yesterday')"
                @click="onYesterdayButtonClick($event)"
                :class="customTodayButtonClasses"
              />
            </div>
          </div>
          <div class="flex flex-wrap">
            <div class="w-full md:w-6 p-1 md:p-2">
              <Button
                :label="translate('calendar.currentWeekLabel', 'Current Week')"
                @click="onCurrentWeekButtonClick($event)"
                :class="customTodayButtonClasses"
              />
            </div>
            <div class="w-full md:w-6 p-1 md:p-2">
              <Button
                :label="translate('calendar.lastWeekLabel', 'Last Week')"
                @click="onLastWeekButtonClick($event)"
                :class="customTodayButtonClasses"
              />
            </div>
          </div>
          <div class="flex flex-wrap">
            <div class="w-full md:w-6 p-1 md:p-2">
              <Button
                :label="translate('calendar.currentMonthLabel', 'Current Month')"
                @click="onCurrentMonthButtonClick($event)"
                :class="customTodayButtonClasses"
              />
            </div>
            <div class="w-full md:w-6 p-1 md:p-2">
              <Button
                :label="translate('calendar.lastMonthLabel', 'Last Month')"
                @click="onLastMonthButtonClick($event)"
                :class="customTodayButtonClasses"
              />
            </div>
          </div>
        </div>
      </template>

      <template #footer v-if="showCustomFooter || showClear">
        <div class="p-datepicker-buttonbar">
          <div class="flex flex-wrap">
            <div class="w-full">
              <Button
                :label="translate('calendar.clear', 'Clear')"
                @click="onClearClick"
                text
                class="w-full"
              />
            </div>
          </div>
        </div>
      </template>
    </PVCalendar>

    <span v-if="$slots.helperText" class="caption">
      <slot name="helperText" />
    </span>
  </div>
</template>

<script setup lang="ts">
import { DateTime } from 'luxon';
import PVCalendar from 'primevue/calendar';
import { ref, useAttrs, type PropType } from 'vue';
import { Button } from '../../buttons';
import { ButtonStateClass } from '../../buttons/enums';
import { useInputState } from '../composables';
import { useTranslation } from './../../../../../helpers';

const props = defineProps({
  label: String,
  helperText: String,
  showCustomFooter: {
    type: Boolean,
    default: false,
  },
  showClear: {
    type: Boolean,
    default: false,
  },
  selectionMode: {
    type: String,
    default: 'single',
  },
  dateFormat: {
    type: String,
    default: 'M dd, yy',
  },
});

defineOptions({
  name: 'Calendar',
  inheritAttrs: false,
});

const emit = defineEmits(['change']);

const value = defineModel({ type: [Array, Date, String] as PropType<Array<Date> | Date | String> });

const attrs = useAttrs();
const { translate } = useTranslation();
const customTodayButtonClasses = `${ButtonStateClass.TEXT} w-full`;

const calendar = ref<InstanceType<typeof PVCalendar>>();

const date = DateTime.local().toJSDate();
const weekStartDate = DateTime.local().startOf('week').minus({ day: 1 });
const weekEndDate = DateTime.local().endOf('week').minus({ day: 1 });

const onTodayButtonClick = (event: Event) => {
  const dates = [date, date];
  // @ts-expect-error FIXME: No overload matches this call
  calendar.value.$emit('today-click', dates);
  // @ts-expect-error FIXME: No overload matches this call
  calendar.value.$emit('date-select', dates);
  (calendar.value as any).overlayVisible = false;
  value.value = dates;
  event.preventDefault();
};

const onYesterdayButtonClick = (event: Event) => {
  const yesterday = DateTime.local().minus({ day: 1 }).toJSDate();
  const dates = [yesterday, yesterday];

  // @ts-expect-error FIXME: No overload matches this call
  calendar.value.$emit('today-click', dates);
  // @ts-expect-error FIXME: No overload matches this call
  calendar.value.$emit('date-select', dates);
  (calendar.value as any).overlayVisible = false;
  value.value = dates;
  event.preventDefault();
};

const onCurrentWeekButtonClick = (event: Event) => {
  const firstDay = weekStartDate.toJSDate();
  const lastDay = weekEndDate.toJSDate();

  let dates = [];

  if (date.getDate() < lastDay.getDate()) {
    dates = [firstDay, date];
  } else {
    dates = [firstDay, lastDay];
  }

  // @ts-expect-error FIXME: No overload matches this call
  calendar.value.$emit('date-select', dates);
  (calendar.value as any).overlayVisible = false;
  value.value = dates;
  event.preventDefault();
};

const onLastWeekButtonClick = (event: Event) => {
  const dates = [
    weekStartDate.minus({ week: 1 }).toJSDate(),
    weekEndDate.minus({ week: 1 }).toJSDate(),
  ];
  // @ts-expect-error FIXME: No overload matches this call
  calendar.value.$emit('date-select', dates);
  (calendar.value as any).overlayVisible = false;
  value.value = dates;
  event.preventDefault();
};

const onCurrentMonthButtonClick = (event: Event) => {
  const firstDay = DateTime.local().startOf('month').toJSDate();
  const lastDay = DateTime.local().endOf('month').toJSDate();

  let dates = [];
  if (date.getMonth() === lastDay.getMonth() && lastDay.getDate() > date.getDate()) {
    dates = [firstDay, date];
  } else {
    dates = [firstDay, lastDay];
  }

  // @ts-expect-error FIXME: No overload matches this call
  calendar.value.$emit('today-click', dates);
  // @ts-expect-error FIXME: No overload matches this call
  calendar.value.$emit('date-select', dates);
  (calendar.value as any).overlayVisible = false;
  value.value = dates;
  event.preventDefault();
};

const onLastMonthButtonClick = (event: Event) => {
  const firstDay = DateTime.local().minus({ month: 1 }).startOf('month').toJSDate();
  const lastDay = DateTime.local().minus({ month: 1 }).endOf('month').toJSDate();

  let dates = [firstDay, lastDay];
  // @ts-expect-error FIXME: No overload matches this call
  calendar.value.$emit('today-click', dates);
  // @ts-expect-error FIXME: No overload matches this call
  calendar.value.$emit('date-select', dates);
  (calendar.value as any).overlayVisible = false;
  value.value = dates;
  event.preventDefault();
};

const onClearClick = (event: any) => {
  // @ts-expect-error FIXME: No overload matches this call
  calendar.value.$emit('date-select', []);
  value.value = [];
  event.preventDefault();
};

const { focused, hovered, classes: rootClasses } = useInputState(attrs);
</script>
